<template>
  <div class="pcg-select" ref="main"
       @keydown.tab.exact.prevent.stop="handleDropdownTab($event)"
       @keydown.tab.shift.prevent.stop="handleDropdownShiftTab($event)"
       @keydown.space.exact.capture.prevent="selectHighlighted($event)"
       @keydown.down.exact.capture.prevent="navigateOptions(1)"
       @keydown.up.exact.capture.prevent="navigateOptions(-1)">
    <label
        v-if="label"
        :class="{ 'pcg-input-label-required': required }"
        class="pcg-input-label"
    >
      {{ label }}
    </label>
    <b-dropdown
        :disabled="disabled"
        lazy
        variant="light"
        class="bootstrap-select"
        @hidden="active = false"
        @toggle="$emit('toggle')"
        ref="dropdownRef"
        @shown="initKeyboardNavigation"
        @keydown.space.exact.capture.prevent="selectHighlighted"
    >
      <template v-slot:button-content>
        <div class="filter-option">
          <div
              class="filter-option-inner d-flex flex-nowrap justify-content-between">
            <div v-if="image" class="image">
              <b-img rounded="circle"
                     :src="getOption(selectedOption).path"
                     height="25"
                     width="25"
                     alt="person">
              </b-img>
            </div>
            <div :class="{'ml-3': image, 'aqua': color === 'aqua'}"
                 class="mr-3 custom-class" :style="{ maxWidth: '86%'}">
              <slot name="button-text"
                    v-bind:text="getOption(selectedOption).text"
                    v-bind:className="getOption(selectedOption).class"
              >
                <span class="ellipsis">
                  {{ getOption(selectedOption).text }}
                </span>
              </slot>
            </div>
            <i v-if="active"
               :class="`${getOption(selectedOption).class}-color`"
               class="fas fa-angle-up arrow"
            />
            <i v-else
               class="fas fa-angle-down arrow"
               :class="`${getOption(selectedOption).class}-color`"
            />
          </div>
        </div>
      </template>
      <slot v-bind:options="selectableOptions" v-bind:change="change">
        <b-dropdown-item v-for="(option, index) in selectableOptions"
                         :active="isOptionActive(option.value)"
                         :key="`${index}_${uuidv4()}`"
                         :ref="`option-${index}`"
                         @click="change(option.value)"
                         v-on="$listeners"
        >
          <div class="d-flex flex-nowrap align-items-baseline">
            <div class="mr-2" v-if="image">
              <b-img rounded="circle"
                     :src="option.path"
                     height="25"
                     width="25"
                     alt="person">
              </b-img>
            </div>
            <slot name="item-text" v-bind:option="option">
              <span :class="option.class" class="prk-fz-12">
                {{ option.text }}
              </span>
            </slot>
          </div>
        </b-dropdown-item>
      </slot>
    </b-dropdown>
    <button v-if="showValueAddition" class="pcg-link add-value-link"
            @click="showModalSuggestion">
      {{ $t('general.add_value') }}
    </button>
    <div v-if="showError" class="pcg-error-messages">{{ errorText }}</div>
  </div>
</template>

<script>

import useHelpers from '../composables/useHelpers'

export default {
  name: 'prk-select',
  setup () {
    const { uuidv4 } = useHelpers()
    return { uuidv4 }
  },
  props: {
    color: {
      type: String,
      default: 'gray',
      validator (value) {
        return ['gray', 'aqua'].includes(value)
      },
    },
    options: {
      type: Array,
      default: () => [],
    },
    value: {
      default: null,
    },
    defaultOption: {
      type: Object,
      default: () => {
        return {
          text: null,
          value: null,
        }
      },
    },
    label: String,
    showError: {
      type: Boolean,
      default: false,
    },
    image: {
      type: Boolean,
      default: false,
    },
    errorText: String,
    required: Boolean,
    showValueAddition: Boolean,
    includeBlank: {
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    reset: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      active: false,
      selectedOption: this.value,
      highlightedIndex: -1,
    }
  },
  methods: {
    showModalSuggestion () {
      this.$emit('showModalSuggestion')
    },
    getOption (value) {
      return this.selectableOptions.find(option => option.value === value) || this.defaultOption
    },
    change (value) {
      this.selectedOption = value
      this.$emit('input', value)
    },
    isOptionActive (value) {
      return this.selectedOption === value
    },
    focusOption (index) {
      this.$nextTick(() => {
        const optionRef = this.$refs[`option-${index}`]
        if (optionRef && optionRef[0]) {
          const innerElement = optionRef[0].$el.querySelector('a') || optionRef[0].$el
          if (innerElement) {
            innerElement.focus()
          }
        }
      })
    },
    initKeyboardNavigation () {
      this.active = true
      this.$nextTick(() => {
        if (this.selectableOptions.length > 0) {
          if (this.selectableOptions.length > 0) {
            let initialIndex = this.selectableOptions.findIndex(o => o.value === this.selectedOption)
            if (initialIndex === -1) initialIndex = 0

            this.highlightedIndex = initialIndex
            this.focusOption(initialIndex)
          }
        }
      })
    },
    navigateOptions (direction) {
      const maxIndex = this.selectableOptions.length - 1
      let newIndex = this.highlightedIndex + direction

      if (newIndex < 0) {
        newIndex = maxIndex
      } else if (newIndex > maxIndex) {
        newIndex = 0
      }

      this.highlightedIndex = newIndex
      this.focusOption(newIndex)
    },
    selectHighlighted (event) {
      event.preventDefault()
      event.stopImmediatePropagation()

      if (this.highlightedIndex !== -1) {
        const option = this.selectableOptions[this.highlightedIndex]
        if (option) {
          this.change(option.value)
          this.$refs.dropdownRef.hide(true) // Zamknij listę po wyborze
        }
      }
    },
    handleDropdownTab (event) {
      this.$refs.dropdownRef.hide(false)
      this.$nextTick(() => {
        const nextTarget = this.findNextFocusableElement(event.target)

        if (nextTarget) {
          event.preventDefault()
          nextTarget.focus()
        }
      })
    },
    handleDropdownShiftTab (event) {
      this.$refs.dropdownRef.hide(false)
      this.$nextTick(() => {
        const prevTarget = this.findPrevFocusableElement(event.target)

        if (prevTarget) {
          event.preventDefault()
          prevTarget.focus()
        }
      })
    },
    findFocusableElements () {
      return Array.from(
        document.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'))
        .filter(el => el.offsetParent !== null)
    },
    findNextFocusableElement (currentElement) {
      const focusableElements = this.findFocusableElements()

      let currentIndex = focusableElements.indexOf(currentElement)
      if (currentIndex === -1) {
        const dropdownButton = this.$refs.dropdownRef.$el.querySelector('button')
        currentIndex = focusableElements.indexOf(dropdownButton)
      }

      let nextElement = null
      while (currentIndex < focusableElements.length) {
        currentIndex++
        nextElement = focusableElements[currentIndex]
        if (nextElement && !this.$refs.main.contains(nextElement)) {
          return nextElement
        }
      }
    },
    findPrevFocusableElement (currentElement) {
      const focusableElements = this.findFocusableElements()

      let currentIndex = focusableElements.indexOf(currentElement)
      if (currentIndex === -1) {
        const dropdownButton = this.$refs.dropdownRef.$el.querySelector('button')
        currentIndex = focusableElements.indexOf(dropdownButton)
      }
      let prevElement = null
      while (currentIndex > 0) {
        currentIndex--
        prevElement = focusableElements[currentIndex]
        if (prevElement && !this.$refs.main.contains(prevElement)) {
          return prevElement
        }
      }
    },
  },
  watch: {
    value (newValue) {
      this.selectedOption = newValue
    },
    defaultOption () {
      if (this.reset) this.selectedOption = ''
    },
    options () {
      if (this.options.length === 0) {
        this.selectedOption = null
      }
    },
  },
  computed: {
    includeBlankOption () {
      if (typeof this.includeBlank === 'boolean') {
        return {
          text: this.$t('general.choose'),
          value: null,
        }
      } else {
        return {
          text: this.includeBlank,
          value: null,
        }
      }
    },
    selectableOptions () {
      const filteredOptions = this.options.filter(e => e.value !== null)
      if (this.includeBlank === false) {
        return filteredOptions
      } else {
        return [this.includeBlankOption].concat(filteredOptions)
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '../assets/stylesheets/vars';

.pcg-select {
  ::v-deep .btn-light {
    width: 100%;
    text-align: left;

    .ellipsis {
      max-width: 100%;
      display: inline-block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &:after {
      position: absolute !important;
      right: 1.5rem;
      top: 50%;
    }

    &:focus {
      box-shadow: $select-box-shadow;
    }
  }

  ::v-deep .dropdown-menu {
    width: auto;

    .dropdown-item {
      white-space: nowrap;
    }

    .dropdown-item.active {
      border-radius: calc(.25rem - 1px);
    }
  }

  .dropdown.show {
    ::v-deep .btn-light {
      background-color: $main-active-color                      ;

      .custom-class {
        color: $pcg-white;
      }
    }
  }

  &.statuses .dropdown.show, &.light .dropdown.show {
    ::v-deep {
      .btn-light {
        background-color: $pcg-white !important;
      }
    }
  }

  .add-value-link {
    display: block;
    font-size: $font-size-s;
    margin-top: $pcg-m-xs;
    background-color: transparent;
    border: none;
    outline: none;
    padding: 0;
  }

  .filter-option-inner {
    /*overflow: hidden;*/
    margin-left: .86rem;
    text-overflow: ellipsis;
    width: calc(100% - 15px);
  }
}

::v-deep {
  button:after {
    display: none !important;
  }

  .pcg-select, .filter-option-inner {
    margin-left: 0 !important;
    width: auto !important;
  }
}

.arrow {
  font-size: 1.4rem;
  color: $pcg-aqua-blue;
  transform: translateY(-.214rem);
}

.image {
  transform: translateY(-.35rem);
}

.aqua {
  color: $pcg-aqua-blue;
  font-weight: bold;
}

.bootstrap-select.show {
  .aqua {
    color: $pcg-white;
  }

  .arrow {
    color: $pcg-white;
  }
}
</style>

<style scoped lang="scss">
@import "../assets/stylesheets/vars-contrast";

.contrast {
  .pcg-select {
    .dropdown.show {
      ::v-deep .btn-light {
        background-color: $main-active-color !important;

        .custom-class {
          color: $pcg-white;
        }
      }
    }

    &.statuses .dropdown.show, &.light .dropdown.show {
      ::v-deep {
        .btn-light {
          background-color: $pcg-white !important;
        }
      }
    }

    .arrow {
      color: $pcg-aqua-blue;
    }

    .aqua {
      color: $pcg-aqua-blue;
    }

    .bootstrap-select.show {
      .aqua {
        color: $pcg-white;
      }

      .arrow {
        color: $pcg-white;
      }
    }
  }
}
</style>

<style lang="scss">
@import '../assets/stylesheets/vars';

.pcg-select.statuses .dropdown-menu {
  .dropdown-item.active {
    background: $pcg-lightest-aqua-blue;
  }
}
</style>

<style lang="scss">
@import "../assets/stylesheets/vars-contrast";

.pcg-select.statuses .dropdown-menu {
  .dropdown-item.active {
    background: $pcg-lightest-aqua-blue;
  }
}
</style>
